/**
 * iOS-native paywall shown by ShopGate when an owner's trial has
 * ended on the iOS app and they have not yet subscribed via either
 * Stripe (web) or Apple IAP (this paywall).
 *
 * This is the surface Apple's reviewers will exercise during App
 * Review with the demo account. To pass review under guideline
 * 3.1.1, it must:
 *
 *   1. Show only Apple-localized prices (Apple injects them via the
 *      RevenueCat package's product.priceString — DO NOT hardcode).
 *   2. Have a working Subscribe button on each tier that completes
 *      a real StoreKit purchase end-to-end.
 *   3. Have a "Restore Purchases" button (3.1.1 mandatory).
 *   4. Surface auto-renew terms + Terms of Service + Privacy Policy
 *      links (3.1.2, mandatory for auto-renewable subs).
 *   5. NOT route to any non-IAP payment mechanism. No "manage on
 *      web" buttons here. (SubscriptionSettingsCard handles that for
 *      already-subscribed iOS users, but that surface is only
 *      reachable AFTER a successful purchase.)
 *
 * On a successful purchase the parent re-runs the gate check, which
 * picks up the new entitlement via hasPremiumEntitlement() (Phase 3
 * webhook will keep Supabase user_subscriptions in sync server-side
 * but until that lands we trust the local RevenueCat cache).
 */
import { useEffect, useState } from "react";
import { Loader2, CreditCard, RotateCcw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import {
  getDefaultOffering,
  purchasePackage,
  restorePurchases,
  findPackageByIdentifier,
  SOLO_PRO_PACKAGE_ID,
  STUDIO_PACKAGE_ID,
  PREMIUM_ENTITLEMENT_ID,
} from "@/lib/revenueCat";
import type {
  PurchasesOffering,
  PurchasesPackage,
} from "@revenuecat/purchases-capacitor";

interface Props {
  /** Called after a successful purchase or restore. The parent should
   * re-check subscription state (e.g. by re-running its gate logic).*/
  onPurchaseSuccess: () => void;
  /** Optional retry handler if the offering fetch fails. */
  onRetry?: () => void;
}

export function IOSPaywall({ onPurchaseSuccess, onRetry }: Props) {
  const { t } = useTranslation("system");
  const [offering, setOffering] = useState<PurchasesOffering | null>(null);
  const [loadingOffering, setLoadingOffering] = useState(true);
  const [offeringError, setOfferingError] = useState<string | null>(null);
  const [purchasingPackage, setPurchasingPackage] = useState<string | null>(
    null,
  );
  const [restoring, setRestoring] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Auto-retry the offering fetch for up to 30s. On a fresh install
  // (and especially on iPad in App Review's clean test environment)
  // StoreKit can take several seconds to propagate product metadata
  // to the device after RevenueCat.configure() + logIn(). A single-
  // shot fetch hits that race window and shows "Subscription
  // unavailable" — which Apple has cited verbatim in rejection
  // rounds 6, 7, and 8 under guideline 2.1(b) App Completeness.
  //
  // We poll every 2s for 15 attempts (30s total), and only treat a
  // result as "loaded" when the offering actually has packages on
  // it. Empty `current`, empty `all`, and "offering with zero
  // packages" all retry the same way. After the budget is exhausted
  // we surface the hard error state so the user can tap "Try again."
  useEffect(() => {
    let cancelled = false;
    let attemptTimer: ReturnType<typeof setTimeout> | null = null;
    let attempts = 0;
    const MAX_ATTEMPTS = 15;
    const INTERVAL_MS = 2000;

    setLoadingOffering(true);
    setOfferingError(null);

    const attempt = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const fetched = await getDefaultOffering();
        if (cancelled) return;
        const hasPackages = Boolean(
          fetched?.availablePackages &&
            fetched.availablePackages.length > 0,
        );
        if (hasPackages) {
          setOffering(fetched);
          setLoadingOffering(false);
          return;
        }
        if (attempts < MAX_ATTEMPTS) {
          attemptTimer = setTimeout(attempt, INTERVAL_MS);
        } else {
          // Budget exhausted — fall through to the error state so
          // the user can try again manually. This is the same copy
          // Apple has seen in prior rejections; it's accurate text
          // for the "we genuinely cannot load products" case.
          setOfferingError(t("iosPaywall.offeringUnavailable"));
          setLoadingOffering(false);
        }
      } catch (e) {
        if (cancelled) return;
        if (attempts < MAX_ATTEMPTS) {
          attemptTimer = setTimeout(attempt, INTERVAL_MS);
        } else {
          setOfferingError(
            (e as { message?: string })?.message ??
              t("iosPaywall.offeringLoadFailed"),
          );
          setLoadingOffering(false);
        }
      }
    };

    attempt();
    return () => {
      cancelled = true;
      if (attemptTimer) clearTimeout(attemptTimer);
    };
  }, []);

  const soloPro = findPackageByIdentifier(offering, SOLO_PRO_PACKAGE_ID);
  const studio = findPackageByIdentifier(offering, STUDIO_PACKAGE_ID);

  const handlePurchase = async (pkg: PurchasesPackage) => {
    setActionError(null);
    setPurchasingPackage(pkg.identifier);
    try {
      const { customerInfo } = await purchasePackage(pkg);
      // Sanity check: the active entitlement should now contain
      // PREMIUM_ENTITLEMENT_ID. If it doesn't, the StoreKit
      // transaction completed but RevenueCat hasn't yet propagated —
      // wait briefly then trust the parent's re-check to settle it.
      const isActive = Boolean(
        customerInfo.entitlements.active[PREMIUM_ENTITLEMENT_ID],
      );
      if (!isActive) {
        // Edge case: surface a brief delay rather than blank-staring.
        await new Promise((r) => setTimeout(r, 800));
      }
      onPurchaseSuccess();
    } catch (e) {
      const err = e as {
        userCancelled?: boolean;
        message?: string;
        code?: string | number;
      };
      // User taps Cancel in the StoreKit sheet — not an error.
      if (err?.userCancelled) {
        setPurchasingPackage(null);
        return;
      }
      setActionError(
        err?.message ??
          t("iosPaywall.purchaseFailed"),
      );
    } finally {
      setPurchasingPackage(null);
    }
  };

  const handleRestore = async () => {
    setActionError(null);
    setRestoring(true);
    try {
      const customerInfo = await restorePurchases();
      const isActive = Boolean(
        customerInfo.entitlements.active[PREMIUM_ENTITLEMENT_ID],
      );
      if (isActive) {
        onPurchaseSuccess();
      } else {
        setActionError(t("iosPaywall.noPreviousPurchase"));
      }
    } catch (e) {
      setActionError(
        (e as { message?: string })?.message ??
          t("iosPaywall.restoreFailed"),
      );
    } finally {
      setRestoring(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    window.location.href = "/";
  };

  if (loadingOffering) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (offeringError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-lg animate-fade-in">
          <div className="bg-card border border-border rounded-lg shadow-vintage p-8 text-center space-y-6">
            <div>
              <h1 className="font-display text-3xl text-foreground mb-2">
                {t("iosPaywall.unavailableTitle")}
              </h1>
              <p className="text-muted-foreground">{offeringError}</p>
            </div>
            <div className="flex flex-col gap-3">
              {onRetry && (
                <button
                  onClick={onRetry}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-4 py-3 font-medium"
                >
                  {t("shared.tryAgain")}
                </button>
              )}
              <button
                onClick={handleSignOut}
                className="text-sm text-muted-foreground hover:text-foreground underline"
              >
                {t("shared.signOut")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-lg animate-fade-in">
        <div className="bg-card border border-border rounded-lg shadow-vintage p-8 space-y-6">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <CreditCard className="w-8 h-8 text-primary" />
            </div>
            <h1 className="font-display text-3xl text-foreground mb-2">
              {t("iosPaywall.chooseTitle")}
            </h1>
            <p className="text-muted-foreground">
              {t("iosPaywall.trialEnded")}
            </p>
          </div>

          {actionError && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 text-sm text-destructive">
              {actionError}
            </div>
          )}

          <div className="space-y-3">
            {soloPro && (
              <PackageButton
                pkg={soloPro}
                label={t("iosPaywall.soloLabel")}
                description={t("iosPaywall.soloDescription")}
                onPurchase={handlePurchase}
                isPurchasing={purchasingPackage === soloPro.identifier}
                disabled={
                  purchasingPackage !== null || restoring
                }
              />
            )}
            {studio && (
              <PackageButton
                pkg={studio}
                label={t("iosPaywall.studioLabel")}
                description={t("iosPaywall.studioDescription")}
                variant="primary"
                onPurchase={handlePurchase}
                isPurchasing={purchasingPackage === studio.identifier}
                disabled={
                  purchasingPackage !== null || restoring
                }
              />
            )}
          </div>

          <p className="text-xs text-muted-foreground text-center leading-relaxed">
            {t("iosPaywall.autoRenew")}
          </p>

          <div className="flex flex-col gap-3 pt-2 border-t border-border">
            <button
              onClick={handleRestore}
              disabled={restoring || purchasingPackage !== null}
              className="flex items-center justify-center gap-2 text-sm text-foreground hover:text-primary transition-colors py-2 disabled:opacity-50"
            >
              {restoring ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RotateCcw className="w-4 h-4" />
              )}
              {t("iosPaywall.restore")}
            </button>

            <div className="flex justify-center gap-4 text-xs text-muted-foreground">
              <a
                href="https://shopledger.app/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
              >
                {t("iosPaywall.terms")}
              </a>
              <span>&middot;</span>
              <a
                href="https://shopledger.app/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
              >
                {t("iosPaywall.privacy")}
              </a>
            </div>

            <button
              onClick={handleSignOut}
              className="text-sm text-muted-foreground hover:text-foreground underline transition-colors"
            >
              {t("shared.signOut")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface PackageButtonProps {
  pkg: PurchasesPackage;
  label: string;
  description: string;
  variant?: "primary" | "secondary";
  isPurchasing: boolean;
  disabled: boolean;
  onPurchase: (pkg: PurchasesPackage) => void;
}

function PackageButton({
  pkg,
  label,
  description,
  variant = "secondary",
  isPurchasing,
  disabled,
  onPurchase,
}: PackageButtonProps) {
  // Apple-localized price string. RevenueCat populates this with the
  // exact text Apple wants displayed for the user's region (currency
  // symbol, decimals, etc.). Never derive your own from the numeric
  // price — Apple's review checks for this.
  const { t } = useTranslation("system");
  const priceText = pkg.product.priceString;
  const isPrimary = variant === "primary";

  return (
    <button
      type="button"
      onClick={() => onPurchase(pkg)}
      disabled={disabled}
      className={`w-full rounded-lg border p-4 text-left transition-colors disabled:opacity-50 ${
        isPrimary
          ? "border-primary bg-primary/5 hover:bg-primary/10"
          : "border-border hover:bg-accent"
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-display text-lg text-foreground">
              {label}
            </span>
            {isPrimary && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-primary text-primary-foreground font-medium">
                {t("iosPaywall.mostPopular")}
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-1">{description}</p>
        </div>
        <div className="flex flex-col items-end shrink-0">
          {isPurchasing ? (
            <Loader2 className="w-5 h-5 animate-spin text-primary" />
          ) : (
            <span className="font-display text-xl text-foreground whitespace-nowrap">
              {priceText}
            </span>
          )}
          <span className="text-xs text-muted-foreground">{t("iosPaywall.perMonth")}</span>
        </div>
      </div>
    </button>
  );
}
